import type { ExecutiveSummaryReportData, TrendPoint } from "@/_store/reports.module";
import type { TicketGeneratingSubscriptionModule } from "@/constants/workplaces";
import type { TicketTrigger } from "@/constants/tickets";
import { OsType } from "@/constants/devices";
import clamp from "lodash/clamp";

export interface ExecutiveSummaryBarListItem {
  /**
   * Stable identity for `v-for` keys. Distinct from `label`, which is a translated string and
   * therefore not unique: two TicketTriggers share one translation in fr-FR, and two OsType
   * members both render as "macOS".
   */
  id: string;
  label: string;
  value: number;
}

export interface ExecutiveSummaryKpiViewModel {
  users: {
    total: number;
    protected: number;
    unProtected: number;
    points: TrendPoint[];
    /** Absent when the backend sent no prior-period baseline — NOT the same as zero. */
    delta?: number;
  };
  devices: {
    total: number;
    /** Partial: {@link mergeMacOsKeys} removes the folded-away key entirely. */
    types: Partial<Record<OsType, number>>;
    outdatedDevices?: Partial<Record<OsType, number>>;
    points: TrendPoint[];
    /** Absent when the backend sent no prior-period baseline — NOT the same as zero. */
    delta?: number;
  };
  tickets: {
    total: number;
    totalUnProcessed: number;
    autoResolvedPercent?: number;
  };
}

export interface ExecutiveSummaryModuleViewModel {
  module: TicketGeneratingSubscriptionModule;
  total: number;
  autoResolvedPercent: number;
  points: TrendPoint[];
  /** Absent when the backend sent no prior-period baseline — which is NOT the same as zero. */
  delta?: number;
  /** Left as a map: sorting needs the translated trigger label, which lives in the component. */
  vulnerabilities: Partial<Record<TicketTrigger, number>>;
  violators: ExecutiveSummaryBarListItem[];
}

/**
 * Folds {@link OsType.OSX} into {@link OsType.MAC_OS}, which mean the same platform.
 * Both translate to the identical label in every locale, so a response carrying both keys — the
 * state the two of them exist for during the client migration — would render two rows named
 * "macOS" with the count split between them. The folded key is dropped rather than zeroed, so it
 * cannot come back as an empty row when "Show 0 values" is on.
 * Rebuilt key by key rather than spread-and-delete: the rows render in the object's insertion
 * order, and assigning MAC_OS onto a copy appends it, which moved macOS to the bottom of the list.
 * @param counts - Per-OS counts as sent, possibly carrying both Mac keys.
 * @returns A copy with both Mac keys folded into `MAC_OS`, kept at the position of whichever came
 * first.
 */
const mergeMacOsKeys = (
  counts?: Partial<Record<OsType, number>>
): Partial<Record<OsType, number>> | undefined => {
  if (!counts || counts[OsType.OSX] === undefined) {
    return counts;
  }
  const macTotal = (counts[OsType.OSX] ?? 0) + (counts[OsType.MAC_OS] ?? 0);
  const merged: Partial<Record<OsType, number>> = {};
  let macPlaced = false;

  (Object.keys(counts) as OsType[]).forEach((key) => {
    if (key !== OsType.OSX && key !== OsType.MAC_OS) {
      merged[key] = counts[key];
      return;
    }
    if (!macPlaced) {
      merged[OsType.MAC_OS] = macTotal;
      macPlaced = true;
    }
  });

  return merged;
};

/**
 * Projects the report payload onto the view model behind the KPI strip.
 * Two things make this more than a field copy. The backend sends `trends.usersAndDevices` as WIDE
 * rows (`{ date, users, devices }`), while each sparkline needs its own `{ date, value }` series, so
 * the rows are transposed here — in one pass, since both series come from the same rows. And
 * `tickets.autoResolvedPercent` is clamped to 0–100 here rather than in a component.
 * @param data - The raw `GET /summary-reports/workspace` payload.
 * @returns A view model whose `tickets.autoResolvedPercent` is `undefined` — not `0` — when the
 * backend omits it or sends `null`, so the caller can hide the caption instead of claiming zero
 * percent were auto-resolved.
 */
export const adaptExecutiveSummaryKpi = (
  data: ExecutiveSummaryReportData
): ExecutiveSummaryKpiViewModel => {
  // EVERY container is optional-chained here, not just `trends`: this runs inside the `kpi`
  // computed whose `undefined` result gates rendering, and a throw there is a render error the
  // `v-if` cannot catch — it takes down the whole card, not just the strip. `trends` was chained
  // and `tickets`, `users` and `devices` were not, so the guard covered one payload shape in four.
  const { items = [], usersDelta, devicesDelta } = data.trends?.usersAndDevices ?? {};
  const { total, totalUnProcessed, autoResolvedPercent } = data.tickets ?? {};

  const userPoints: TrendPoint[] = [];
  const devicePoints: TrendPoint[] = [];
  items.forEach(({ date, users, devices }) => {
    userPoints.push({ date, value: users });
    devicePoints.push({ date, value: devices });
  });

  return {
    users: {
      total: data.users?.total ?? 0,
      protected: data.users?.protected ?? 0,
      unProtected: data.users?.unProtected ?? 0,
      points: userPoints,
      delta: usersDelta,
    },
    devices: {
      total: data.devices?.total ?? 0,
      types: mergeMacOsKeys(data.devices?.types) ?? {},
      outdatedDevices: mergeMacOsKeys(data.devices?.outdatedDevices),
      points: devicePoints,
      delta: devicesDelta,
    },
    tickets: {
      total: total ?? 0,
      totalUnProcessed: totalUnProcessed ?? 0,
      // Clamped the way ReportCompletionRing clamps its own percentage, so a backend value that
      // drifts past a period boundary can never print "150% Auto-Resolved" or "-20%". Gated on
      // the TYPE, not on `!== undefined`: the backend serializes a missing measurement as `null`,
      // and `Math.max(0, null)` is `0`, which would print "0% Auto-Resolved" for a value that was
      // never measured. Still `undefined`, never `0`, so the caller hides the caption instead.
      autoResolvedPercent:
        typeof autoResolvedPercent === "number" ? clamp(autoResolvedPercent, 0, 100) : undefined,
    },
  };
};

/**
 * Builds one uniform per-module section view model out of the two places a module's data lives:
 * `protection` (counters) and `trends.tickets` (series and delta).
 * A module the workspace is not subscribed to is ABSENT from both, so every lookup here is
 * optional and zero-filled. That is deliberate: it lets the section component render without
 * guarding.
 * @param data - The raw `GET /summary-reports/workspace` payload.
 * @param module - Which ticket-generating module to project.
 * @returns A view model with `violators` converted from the backend's `Record<string, number>` map
 * into an array sorted by count descending, and `delta` left `undefined` when the backend sent no
 * baseline to compare against.
 */
export const adaptExecutiveSummaryModule = (
  data: ExecutiveSummaryReportData,
  module: TicketGeneratingSubscriptionModule
): ExecutiveSummaryModuleViewModel => {
  // Chained like everything else in here: the doc comment above promises every lookup is optional
  // and zero-filled so the section component need not guard, and this was the one line that was
  // not — a payload without `protection` threw inside the `viewModels` computed and blanked the
  // whole card.
  const section = data.protection?.[module];
  const violators = section?.violators ?? {};
  const ticketTrends = data.trends?.tickets;

  return {
    module,
    total: section?.totalVulnerabilities ?? 0,
    autoResolvedPercent: section?.autoResolvedPercent ?? 0,
    // Indexed, not rest-spread: spreading copied every module's key out of each row only to read
    // one back, once per module — six passes each allocating six keys per date bucket.
    points: (ticketTrends?.items ?? []).map((row) => ({ date: row.date, value: row[module] ?? 0 })),
    // Deliberately not `?? 0`: a module with no prior-period baseline has no delta, and zero would
    // make the chart claim "no change" about a comparison that was never made.
    delta: ticketTrends?.deltas?.[module],
    vulnerabilities: section?.vulnerabilities ?? {},
    violators: Object.entries(violators)
      .map(([label, value]) => ({ id: label, label, value }))
      .sort((a, b) => b.value - a.value),
  };
};
